import { Component, ElementRef, OnInit, OnChanges, Input, Output, EventEmitter, ContentChildren, QueryList } from '@angular/core';
import { CfCoreComponent } from '../core/core.component';
import { RatingModel } from '../../models/rating/rating.model';
import { RatingStylingModel } from '../../models/rating/rating-styling.model';
import { RatingTemplates } from '../../templates/rating.template';
import { TemplateService } from '../../services/template-service/template.service';
/**
 * <p>Rating component which has vertical/horizontal icons, icon order changing and other basic functionality.</p>
 * <p>Example of using Menu:</p>
 * <pre>
 * <code><</code>cf-rating [properties]="myRating" [styling]="myRatingStyles"<code>></code>
 * <code><</code><code>/</code>cf-rating<code>></code>
 * </pre>
 */
export class CfRatingComponent extends CfCoreComponent implements OnInit {
/**
 * It is an array to be filled with items according to MAX property
 */
cfItems: number[] = [];
/**
 * It is an attribute <b>[properties]</b> of directive <b>cf-rating</b> which take RatingModel object.
 * <p>Example of Rating json object:</p>
 * <code><pre>{{'{'}}
 *  label: 'Rating label',
 *  icon: 'stars',
 *  value: 1,
 *  max: 5,
 *  countFromEnd: false,
 *  iconsVertical: false
 *  disable: false
 * {{'}'}}</pre></code>
 * <h4>label: string</h4>
 * Label for component
 * <h4>icon: string</h4>
 * Name of icon
 * <h4>value: number</h4>
 * Value of rating and means number of filled/selected icons
 * <h4>max: number</h4>
 * Number of rating icons
 * <h4>countFromEnd: boolean</h4>
 * Means to start icons counting order
 * <h4>iconsVertical: boolean</h4>
 * Means to show icons vertically
 * <h4>disable: boolean</h4>
 * Means component to be disabled
 */
properties: RatingModel;
/**
 * It is an object with styling component elements RatingStylingModel object
 */
styling: RatingStylingModel;
/**
 * Value of rating
 * @param {?} value
 * @return {?}
 */
set value(value: number) { this.properties.value = value; }
/**
 * @hidden
 * @param {?} elementRef
 * @param {?} templateService
 */
constructor(public elementRef: ElementRef, /**@hidden */ templateService: TemplateService) {
    super(elementRef, templateService);
    this.properties = new RatingModel();
    this.styling = new RatingStylingModel();
  }
/**
 * @hidden 
 * Initialize all data
 * @return {?}
 */
ngOnInit() {
    this.getMyTemplate('rating', RatingTemplates).then(() => {
      this.mergeProperties(this.activeTemplate.property, this.properties, 'RATING').then( sampler => {
        let /** @type {?} */ active = new RatingStylingModel(this.activeTemplate.style);
        this.styling = Object.assign(active, this.styling);
        for (let /** @type {?} */ i = 0; i < this.properties.max; ++i) {
          this.cfItems.push(i);
        }
        // console.log('RATING', this);
      });

    });
  }
/**
 * @hidden 
 * Function to set component value
 * @param {?} index  it is used to calculate component value
 * @return {?}
 */
private setRating(index) {
    if (!this.properties.disable) { this.properties.value = index + 1; }
  }
/**
 * @hidden 
 * Function to unselect current item by user double-click and change component value
 * @param {?} index  it is used to calculate component value
 * @return {?}
 */
private resetCurrentItem(index) {
    if (!this.properties.disable) { this.properties.value = index; }
  }
/**
 * @hidden 
 * Function to set filled color for icons
 * @return {?}
 */
private getHighlitedColor() {
    return !this.properties.disable ? this.styling.filledColor : 'rgba(0,0,0,0.5)';
  }
/**
 * @hidden 
 * Function to set empty color for icons
 * @return {?}
 */
private getEmptyColor() {
    return !this.properties.disable ? this.styling.emptyColor : 'rgba(0,0,0,0.2)';
  }
static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
  selector: 'cf-rating',
  template: "<div *ngIf=\"_inited && properties?.display\" class=\"cf-rating {{styling?.container?.class}}\" [ngClass]=\"getDynamicClasses(styling?.container?.dynamicClass)\"> <div class=\"cf-rating-label {{properties?.disable ? 'disable':''}} {{styling?.label?.class}}\" [hidden]=\"!properties?.label\" [ngClass]=\"getDynamicClasses(styling?.label?.dynamicClass)\">{{properties?.label}}</div> <div class=\"cf-rating-items {{properties?.iconsVertical ? 'icons-vertical':''}} {{properties?.countFromEnd ? 'count-from-end':''}}\"> <span class=\"cf-rating-item {{properties?.disable ? 'disable':''}} {{styling?.item?.class}}\" *ngFor=\"let item of cfItems, let i = index\" [ngStyle]=\"{'width': styling?.iconSize, 'height': styling?.iconSize}\" [ngClass]=\"getDynamicClasses(styling?.item?.dynamicClass)\"> <cf-icon [styling]=\"styling?.icon\" [ngStyle]=\"{'color': (i+1 <= properties?.value) ? getHighlitedColor() : getEmptyColor()}\"  [name]=\"properties?.icon\" [size]=\"styling?.iconSize\" (click)=\"setRating(i)\" (dblclick)=\"resetCurrentItem(i)\"></cf-icon> </span> </div> </div>",
  styles: [".cf-rating { display: inline-flex; flex-direction: column; } .cf-rating-label { display: flex; color: rgba(0, 0, 0, 0.38); font-size: 12px; } .cf-rating-items { display: flex; align-items: center; justify-content: center; } .cf-rating-items.count-from-end:not(.icons-vertical) { flex-direction: row-reverse; } .cf-rating-items.count-from-end.icons-vertical { flex-direction: column; } .cf-rating-items.icons-vertical { flex-direction: column-reverse; } .cf-rating-item { display: flex; border-radius: 50%; } .cf-rating-item /deep/ md-icon { color: inherit; transition: ease 0.15s all; } .cf-rating-item.disable /deep/ md-icon { cursor: default !important; } .cf-rating-item:not(.disable):hover /deep/ md-icon { transform: scale(1.1); } .cf-rating-label:not(.disable) { color: darkseagreen; } [hidden] { display: none !important; } "]
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: ElementRef, },
{type: TemplateService, },
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'properties': [{ type: Input },],
'styling': [{ type: Input },],
'value': [{ type: Input, args: ['value', ] },],
};
}

function CfRatingComponent_tsickle_Closure_declarations() {
/** @type {?} */
CfRatingComponent.decorators;
/**
 * @nocollapse
 * @type {?}
 */
CfRatingComponent.ctorParameters;
/** @type {?} */
CfRatingComponent.propDecorators;
/**
 * It is an array to be filled with items according to MAX property
 * @type {?}
 */
CfRatingComponent.prototype.cfItems;
/**
 * It is an attribute <b>[properties]</b> of directive <b>cf-rating</b> which take RatingModel object.
 * <p>Example of Rating json object:</p>
 * <code><pre>{{'{'}}
 *  label: 'Rating label',
 *  icon: 'stars',
 *  value: 1,
 *  max: 5,
 *  countFromEnd: false,
 *  iconsVertical: false
 *  disable: false
 * {{'}'}}</pre></code>
 * <h4>label: string</h4>
 * Label for component
 * <h4>icon: string</h4>
 * Name of icon
 * <h4>value: number</h4>
 * Value of rating and means number of filled/selected icons
 * <h4>max: number</h4>
 * Number of rating icons
 * <h4>countFromEnd: boolean</h4>
 * Means to start icons counting order
 * <h4>iconsVertical: boolean</h4>
 * Means to show icons vertically
 * <h4>disable: boolean</h4>
 * Means component to be disabled
 * @type {?}
 */
CfRatingComponent.prototype.properties;
/**
 * It is an object with styling component elements RatingStylingModel object
 * @type {?}
 */
CfRatingComponent.prototype.styling;
/** @type {?} */
CfRatingComponent.prototype.elementRef;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
